import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { projectsAtom } from '../v0/projectAtoms';
import { tasksAtom } from '../v0/taskAtoms';
import { foldersAtom } from './folderAtoms';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { sortTasksByTime } from '../../utils/taskSortUtils';

/**
 * ProjectV2 页面状态管理
 */

// ProjectV2 的核心状态类型
export interface ProjectV2State {
  projectViewMode: 'compact' | 'expanded' | 'card' | 'tabs';
  showCompletedTasks: boolean; // 是否显示已完成任务（适用于expanded/card/tabs模式）
  showCompletedProjects: boolean; // 是否显示已完成项目（适用于所有模式）
  expandedFolderIds: string[]; // 展开的文件夹ID列表
}

// 默认状态
const defaultProjectV2State: ProjectV2State = {
  projectViewMode: 'compact',
  showCompletedTasks: false, // 默认隐藏已完成任务
  showCompletedProjects: false, // 默认隐藏已完成项目
  expandedFolderIds: [] // 空数组，将在下面的原子中动态处理为全部展开
};

// 持久化的状态
const persistedProjectV2StateAtom = atomWithStorage<ProjectV2State>('projectV2State', defaultProjectV2State);

// 项目视图模式原子
export const projectV2ProjectViewModeAtom = atom(
  (get) => get(persistedProjectV2StateAtom).projectViewMode,
  (get, set, projectViewMode: 'compact' | 'expanded' | 'card' | 'tabs') => {
    const state = get(persistedProjectV2StateAtom);
    set(persistedProjectV2StateAtom, { ...state, projectViewMode });
  }
);

// 显示已完成任务原子
export const projectV2ShowCompletedTasksAtom = atom(
  (get) => get(persistedProjectV2StateAtom).showCompletedTasks,
  (get, set, showCompletedTasks: boolean) => {
    const state = get(persistedProjectV2StateAtom);
    set(persistedProjectV2StateAtom, { ...state, showCompletedTasks });
  }
);

// 显示已完成项目原子
export const projectV2ShowCompletedProjectsAtom = atom(
  (get) => get(persistedProjectV2StateAtom).showCompletedProjects,
  (get, set, showCompletedProjects: boolean) => {
    const state = get(persistedProjectV2StateAtom);
    set(persistedProjectV2StateAtom, { ...state, showCompletedProjects });
  }
);

// 项目统计信息类型
export interface ProjectStats {
  totalTasks: number;
  completedTasks: number;
  vitalTasks: number;
  urgentTasks: number;
  completionRate: number;
}

// 带统计信息的项目类型
export interface ProjectWithStats extends Project {
  stats: ProjectStats;
  tasks: Task[];
}

// 计算项目统计信息的辅助函数
const calculateProjectStats = (tasks: Task[]): ProjectStats => {
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter(task => task.done).length;
  const vitalTasks = tasks.filter(task => task.vital).length;
  const urgentTasks = tasks.filter(task => task.urgent).length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  return {
    totalTasks,
    completedTasks,
    vitalTasks,
    urgentTasks,
    completionRate
  };
};

// 带统计信息的项目列表原子
export const projectsWithStatsAtom = atom<ProjectWithStats[]>((get) => {
  const projects = get(projectsAtom);
  const allTasks = get(tasksAtom);

  return projects.map(project => {
    // 获取属于该项目的任务
    const projectTasks = allTasks.filter(task => task.projectId === project.id);

    // 按时间排序任务
    const sortedTasks = sortTasksByTime(projectTasks);

    // 计算统计信息
    const stats = calculateProjectStats(projectTasks);

    return {
      ...project,
      stats,
      tasks: sortedTasks
    };
  });
});

// 按完成状态分组的项目原子
export const projectsGroupedByCompletionAtom = atom((get) => {
  const projectsWithStats = get(projectsWithStatsAtom);

  const incomplete: ProjectWithStats[] = [];
  const completed: ProjectWithStats[] = [];

  projectsWithStats.forEach(project => {
    if (project.stats.completionRate === 100) {
      completed.push(project);
    } else {
      incomplete.push(project);
    }
  });

  return {
    incomplete,
    completed
  };
});

// 重要性分组类型
export interface ImportanceCategory {
  vital: boolean;
  urgent: boolean;
  label: string;
}



// 文件夹展开状态管理原子
export const projectV2ExpandedFolderIdsAtom = atom(
  (get) => {
    const persistedState = get(persistedProjectV2StateAtom);
    const folders = get(foldersAtom);

    // 如果持久化状态中有展开的文件夹ID，使用它们
    if (persistedState.expandedFolderIds && persistedState.expandedFolderIds.length > 0) {
      return persistedState.expandedFolderIds;
    }

    // 否则默认展开所有文件夹（包括ungrouped）
    const allFolderIds = folders.map(folder => folder.id);
    return ['ungrouped', ...allFolderIds];
  },
  (get, set, expandedFolderIds: string[]) => {
    const state = get(persistedProjectV2StateAtom);
    set(persistedProjectV2StateAtom, { ...state, expandedFolderIds });
  }
);

// 切换文件夹展开状态的原子
export const toggleFolderExpandedAtom = atom(
  null,
  (get, set, folderId: string) => {
    const expandedFolderIds = get(projectV2ExpandedFolderIdsAtom);
    const isExpanded = expandedFolderIds.includes(folderId);

    if (isExpanded) {
      // 折叠文件夹
      set(projectV2ExpandedFolderIdsAtom, expandedFolderIds.filter(id => id !== folderId));
    } else {
      // 展开文件夹
      set(projectV2ExpandedFolderIdsAtom, [...expandedFolderIds, folderId]);
    }
  }
);

// 触发创建项目的全局状态
// 每次递增这个值都会触发创建项目模态框
export const triggerCreateProjectAtom = atom(0);
